import { useState, useEffect, useCallback } from "react";

import {
  Close as CloseIcon,
  Publish as PublishIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Badge,
} from "@mui/material";
import {
  getOrgDrafts,
  postBulkDeploy,
  getSurveyVersionCompare,
} from "@rubi/api-generated/clientRs/sdk.gen";
import type {
  DraftSummary,
  BulkDeployResult,
  GetSurveyVersionCompareResponse,
  ComputedDiff,
} from "@rubi/api-generated/clientRs/types.gen";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";

import { VersionComparison } from "@/components/online-reporting/form-management/VersionComparison";

/**
 * Check if a computed diff has any actual changes (added or removed lines)
 */
const hasChanges = (diff: ComputedDiff | undefined): boolean => {
  if (!diff) return false;
  return diff.left_lines.some(
    (line) => line.type === "added" || line.type === "removed",
  );
};

type ViewMode = "list" | "detail" | "results";

interface BulkDeployModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onDeploySuccess: () => void;
}

export const BulkDeployModal: React.FC<BulkDeployModalProps> = ({
  open,
  onClose,
  orgId,
  onDeploySuccess,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [isLoading, setIsLoading] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);

  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [detailSurveyId, setDetailSurveyId] = useState<string | null>(null);
  const [detailDiff, setDetailDiff] =
    useState<GetSurveyVersionCompareResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [versionBump, setVersionBump] = useState<"major" | "minor">("minor");
  const [deployMessage, setDeployMessage] = useState("");
  const [isCondensedForm, setIsCondensedForm] = useState(false);

  const [results, setResults] = useState<BulkDeployResult[] | null>(null);

  const fetchDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrgDrafts({
        path: { org_id: orgId },
      });
      const fetchedDrafts = (response.data?.drafts as DraftSummary[]) || [];
      setDrafts(fetchedDrafts);
      // Select all by default
      setSelectedIds(new Set(fetchedDrafts.map((d) => d.parent_survey_id)));
    } catch (error) {
      console.error("Error fetching drafts:", error);
      toast.error("Failed to load drafts");
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setViewMode("list");
      setSelectedIds(new Set());
      setDetailSurveyId(null);
      setDetailDiff(null);
      setVersionBump("minor");
      setDeployMessage("");
      setIsCondensedForm(false);
      setResults(null);
      fetchDrafts();
    }
  }, [open, fetchDrafts]);

  const handleToggleSelection = (surveyId: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(surveyId)) {
      newSelection.delete(surveyId);
    } else {
      newSelection.add(surveyId);
    }
    setSelectedIds(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(drafts.map((d) => d.parent_survey_id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleViewDetails = async (draft: DraftSummary) => {
    setDetailSurveyId(draft.parent_survey_id);
    setViewMode("detail");
    setDetailLoading(true);

    try {
      // For new surveys without an active version, we can't show a comparison
      if (!draft.active_version_id) {
        setDetailDiff(null);
        setDetailLoading(false);
        return;
      }

      // Compare active version (from) vs draft (to)
      const response = await getSurveyVersionCompare({
        path: {
          org_id: orgId,
          parent_survey_id: draft.parent_survey_id,
        },
        query: {
          from_version_id: draft.active_version_id,
          to_version_id: draft.draft_id,
        },
      });
      setDetailDiff(response.data ?? null);
    } catch (error) {
      console.error("Error fetching diff:", error);
      toast.error("Failed to load version comparison");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setDetailSurveyId(null);
    setDetailDiff(null);
  };

  const handleDeploy = async () => {
    if (selectedIds.size === 0) return;

    setIsDeploying(true);
    try {
      const response = await postBulkDeploy({
        path: { org_id: orgId },
        body: {
          survey_ids: Array.from(selectedIds),
          version_bump: versionBump,
          change_summary: deployMessage || undefined,
        },
      });

      setResults(response.data?.results as BulkDeployResult[]);
      setViewMode("results");

      if (response.data?.failed_count === 0) {
        toast.success(
          `Successfully deployed ${response.data?.success_count} survey(s)`,
        );
      } else {
        toast.warning(
          `Deployed ${response.data?.success_count} survey(s), ${response.data?.failed_count} failed`,
        );
      }
    } catch (error: any) {
      console.error("Error deploying:", error);
      toast.error("Failed to deploy surveys");
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDone = () => {
    onDeploySuccess();
  };

  const formatDraftAge = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else {
      return "Just now";
    }
  };

  const selectedDraft = drafts.find(
    (d) => d.parent_survey_id === detailSurveyId,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <PublishIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Bulk Deploy
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        {/* Loading State */}
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && drafts.length === 0 && viewMode === "list" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No outstanding drafts to deploy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All surveys are up to date.
            </Typography>
          </Box>
        )}

        {/* List View */}
        {!isLoading && drafts.length > 0 && viewMode === "list" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {drafts.length} Survey{drafts.length !== 1 ? "s" : ""} with
                Drafts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedIds.size} of {drafts.length} selected
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedIds.size > 0 &&
                          selectedIds.size < drafts.length
                        }
                        checked={
                          drafts.length > 0 &&
                          selectedIds.size === drafts.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Current Version</TableCell>
                    <TableCell>Draft Age</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drafts.map((draft) => (
                    <TableRow
                      key={draft.parent_survey_id}
                      hover
                      onClick={() =>
                        handleToggleSelection(draft.parent_survey_id)
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(draft.parent_survey_id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleToggleSelection(draft.parent_survey_id)
                          }
                        />
                      </TableCell>
                      <TableCell>{draft.survey_name}</TableCell>
                      <TableCell>
                        {draft.current_version ? (
                          <Chip
                            label={`v${draft.current_version}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="New" size="small" color="info" />
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDraftAge(draft.draft_created_at)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(draft);
                          }}
                        >
                          View Diff
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Deploy Options */}
            <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Deploy Options
              </Typography>

              <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    Version Bump
                  </Typography>
                  <ToggleButtonGroup
                    value={versionBump}
                    exclusive
                    onChange={(_, value) => value && setVersionBump(value)}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value="minor">Minor</ToggleButton>
                    <ToggleButton value="major">Major</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ flex: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Deploy Message (Optional)
                  </Typography>
                  <TextField
                    value={deployMessage}
                    onChange={(e) => setDeployMessage(e.target.value)}
                    placeholder="Describe what changed..."
                    size="small"
                    fullWidth
                  />
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                All selected surveys will be deployed with{" "}
                {versionBump === "minor" ? "minor" : "major"} version bump.
              </Alert>
            </Box>
          </Box>
        )}

        {/* Detail View */}
        {viewMode === "detail" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToList}
                >
                  Back to List
                </Button>
                <Typography variant="h6">
                  {selectedDraft?.survey_name || "Survey Details"}
                </Typography>
              </Box>

              {/* Main/Condensed toggle */}
              {detailDiff && (
                <ToggleButtonGroup
                  color="primary"
                  value={isCondensedForm}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setIsCondensedForm(newValue);
                    }
                  }}
                  aria-label="Form Type"
                  size="small"
                >
                  <ToggleButton
                    value={false}
                    sx={{
                      minWidth: 90,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: colors.common.white,
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <Badge
                      color="success"
                      variant="dot"
                      invisible={!hasChanges(detailDiff.configuration_diff)}
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -6,
                          top: 2,
                        },
                      }}
                    >
                      Main
                    </Badge>
                  </ToggleButton>
                  <ToggleButton
                    value={true}
                    sx={{
                      minWidth: 90,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: colors.common.white,
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <Badge
                      color="success"
                      variant="dot"
                      invisible={!hasChanges(detailDiff.pdf_configuration_diff)}
                      sx={{
                        "& .MuiBadge-badge": {
                          right: -6,
                          top: 2,
                        },
                      }}
                    >
                      Condensed
                    </Badge>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {detailLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={300}
              >
                <CircularProgress />
              </Box>
            ) : detailDiff ? (
              <Box sx={{ height: 500 }}>
                <VersionComparison
                  fromVersion={{
                    id: detailDiff.from_version?.id || "",
                    parent_survey_id: selectedDraft?.parent_survey_id || "",
                    major_version: null,
                    minor_version: null,
                    configuration: {},
                    pdf_configuration: {},
                  }}
                  toVersion={{
                    id: detailDiff.to_version?.id || "",
                    parent_survey_id: selectedDraft?.parent_survey_id || "",
                    major_version: null,
                    minor_version: null,
                    configuration: {},
                    pdf_configuration: {},
                  }}
                  viewType={
                    isCondensedForm ? "pdf_configuration" : "configuration"
                  }
                  configurationDiff={detailDiff.configuration_diff}
                  pdfConfigurationDiff={detailDiff.pdf_configuration_diff}
                />
              </Box>
            ) : (
              <Alert severity="info">
                {selectedDraft?.active_version_id
                  ? "Unable to load version comparison."
                  : "This is a new survey without an active version. The entire configuration will be deployed as v1.0."}
              </Alert>
            )}
          </Box>
        )}

        {/* Results View */}
        {viewMode === "results" && results && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Deployment Results
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${results.filter((r) => r.success).length} Succeeded`}
                color="success"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`${results.filter((r) => !r.success).length} Failed`}
                color={
                  results.filter((r) => !r.success).length > 0
                    ? "error"
                    : "default"
                }
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>New Version</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.parent_survey_id}>
                      <TableCell>{result.survey_name}</TableCell>
                      <TableCell>
                        {result.success ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Success"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Failed"
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {result.new_version ? `v${result.new_version}` : "-"}
                      </TableCell>
                      <TableCell>
                        {result.error ? (
                          <Typography variant="body2" color="error">
                            {result.error}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {viewMode === "list" && drafts.length > 0 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDeploy}
              disabled={isDeploying || selectedIds.size === 0}
              startIcon={
                isDeploying ? <CircularProgress size={16} /> : <PublishIcon />
              }
            >
              {isDeploying
                ? "Deploying..."
                : `Deploy ${selectedIds.size} Survey${selectedIds.size !== 1 ? "s" : ""}`}
            </Button>
          </>
        )}

        {viewMode === "list" && drafts.length === 0 && (
          <Button onClick={onClose}>Close</Button>
        )}

        {viewMode === "detail" && (
          <Button onClick={handleBackToList}>Back to List</Button>
        )}

        {viewMode === "results" && (
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
